import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ListingFeedResponse } from '@marstein-radar/shared';
import { formatEur } from '../styles';

const CATEGORY_LABEL: Record<string, string> = {
  nehnutelnosti: 'Nehnuteľnosti',
  stroje_vybavenie: 'Stroje a vybavenie',
  zasoby_tovar: 'Zásoby/tovar',
  firmy_podiely: 'Firmy/podiely',
  vozidla: 'Vozidlá',
  ostatne: 'Ostatné',
};

// Fáza 2 inzertnej sekcie (2026-07-15) — skutočné user-submitted inzeráty majetku,
// nadväzuje na InsolvencyFeedPage.tsx (Fáza 1, read-only prehľad). Kostra
// (per-field useState, submittedKey gate, useQuery) kopíruje ten istý vzor, ale
// výsledky sú card grid, nie tabuľka (obrázky/cena/titulok). isInsolvencyRelated je
// len jeden z filtrov — jeden spoločný zoznam, žiadna oddelená insolvenčná sekcia
// (viď /root/.claude/plans/abstract-finding-unicorn.md).
export function ListingsFeedPage() {
  const navigate = useNavigate();

  const { data: regions } = useQuery<string[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/regions');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [isInsolvencyRelated, setIsInsolvencyRelated] = useState(false);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [page, setPage] = useState(1);
  const [submittedKey, setSubmittedKey] = useState<string | null>('initial');

  const { data, isFetching } = useQuery<ListingFeedResponse>({
    queryKey: ['listings-feed', submittedKey, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (city.trim()) params.set('city', city.trim());
      if (region) params.set('region', region);
      if (isInsolvencyRelated) params.set('isInsolvencyRelated', 'true');
      if (priceMin) params.set('priceMin', String(Number(priceMin) * 100));
      if (priceMax) params.set('priceMax', String(Number(priceMax) * 100));
      params.set('page', String(page));
      const res = await fetch(`/api/v1/listings/feed?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: submittedKey !== null,
  });

  function submit(): void {
    setPage(1);
    setSubmittedKey(
      `${category}|${city}|${region}|${isInsolvencyRelated}|${priceMin}|${priceMax}|${Date.now()}`,
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="page">
      <div className="page-header">
        <h1>Inzeráty</h1>
        <p>
          Majetok na predaj od používateľov appky Corpuls — nehnuteľnosti, stroje, zásoby aj firmy.
        </p>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Inzeráty pridávajú priamo používatelia — Corpuls <strong>neoveruje</strong> právo predajcu
        majetok predať. Pri majetku z konkurznej podstaty (označenie "súvisí s insolvenciou") môže
        platiť predpísaný postup speňaženia cez správcu podľa zákona o konkurze a reštrukturalizácii
        — informujte sa pred kúpou priamo u predajcu/správcu.
      </div>

      <div className="search-filter-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="filter-grid">
            <label className="filter-field">
              <span>Kategória</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Všetky kategórie</option>
                {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field">
              <span>Mesto / obec</span>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="napr. Košice"
              />
            </label>
            <label className="filter-field">
              <span>Kraj</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">Všetky kraje</option>
                {regions?.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Cena od (€)</span>
              <input
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                type="number"
                min="0"
              />
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Cena do (€)</span>
              <input
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                type="number"
                min="0"
              />
            </label>
            <label
              className="filter-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                type="checkbox"
                checked={isInsolvencyRelated}
                onChange={(e) => setIsInsolvencyRelated(e.target.checked)}
              />
              <span>Súvisí s insolvenciou</span>
            </label>
          </div>
          <button type="submit" className="search-filter-submit">
            Hľadať
          </button>
        </form>
      </div>

      {isFetching && !data && (
        <div className="listing-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="listing-card" key={i} style={{ cursor: 'default' }}>
              <div className="listing-card-image-placeholder skel" />
              <div className="listing-card-body">
                <div className="skel line1" />
                <div className="skel line2" />
              </div>
            </div>
          ))}
        </div>
      )}
      {data && data.results.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne inzeráty nenájdené pre zadané filtre.
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>Výsledky</h2>
            <span className="hint">{data.total.toLocaleString('sk-SK')} inzerátov spolu</span>
          </div>
          <div className="listing-grid">
            {data.results.map((l) => (
              <div
                key={l.id}
                className="listing-card"
                onClick={() => navigate(`/inzeraty/${l.id}`)}
              >
                {l.images.length > 0 ? (
                  <img
                    className="listing-card-image"
                    src={`/uploads/${l.images[0]!.filename}`}
                    alt={l.title}
                  />
                ) : (
                  <div className="listing-card-image-placeholder">Bez fotky</div>
                )}
                <div className="listing-card-body">
                  <div className="listing-card-meta">
                    {CATEGORY_LABEL[l.category] ?? l.category}
                  </div>
                  <div>{l.title}</div>
                  <div className="listing-card-price">
                    {l.priceCents != null ? formatEur(l.priceCents / 100) : 'Cena dohodou'}
                  </div>
                  <div className="listing-card-meta">
                    {l.city ?? '—'}
                    {l.isInsolvencyRelated && ' · súvisí s insolvenciou'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div
              style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem' }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="search-filter-submit"
                style={{ opacity: page <= 1 ? 0.5 : 1 }}
              >
                ← Predošlá
              </button>
              <span style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Strana {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="search-filter-submit"
                style={{ opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Ďalšia →
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
